<template>
    <PageHeader :crumbs="[{ label: $t('portal.products.title') }]" />

    <div class="tw-p-2">
        <div class="tw-h-full tw-w-full tw-p-4 tw-bg-white tw-rounded tw-border tw-border-b-2 tw-min-h-[calc(100dvh-125px)]">
            <div class="tw-justify-between tw-flex tw-items-center tw-gap-2">
                <FormField v-model="search" input-dir="ltr" :placeholder="$t('portal.products.search')"
                    left-icon="clarity:search-line" type="text" class="tw-min-w-[100px]" hide-errors @keyup.enter="fetch(1)" />
                <a-button left-icon="heroicons:plus-16-solid" @click="openCreate">{{ $t('buttons.create') }}</a-button>
            </div>

            <div class="tw-mt-5">
                <DataTable :columns="columns" :loading="loading" :count="items.length">
                    <tr v-for="p in items" :key="p.id + '-' + p.updated_at" :class="rowClass">
                        <td :class="cellClass"><span class="tw-font-medium tw-text-gray-800">{{ p.name }}</span></td>
                        <td :class="cellClass"><span class="tw-capitalize">{{ p.type }}</span></td>
                        <td :class="cellClass">
                            <span v-if="p.category_name" class="tw-inline-flex tw-items-center tw-gap-1 tw-px-2 tw-py-0.5 tw-rounded tw-bg-primary-50 tw-text-primary-600 tw-text-xs tw-font-medium tw-border tw-border-solid tw-border-primary-100">{{ p.category_name }}</span>
                            <span v-else class="tw-text-gray-300">—</span>
                        </td>
                        <td :class="cellClass"><span class="tw-text-gray-500">{{ p.sku || '—' }}</span></td>
                        <td :class="cellClass">{{ fmt.currency(p.selling_price) }}</td>
                        <td :class="cellClass"><span class="tw-text-gray-500">{{ p.cost != null ? fmt.currency(p.cost) : '—' }}</span></td>
                        <td :class="cellClass"><StatusBadge :status="p.status" /></td>
                        <td :class="cellClass">
                            <div class="tw-flex tw-items-center tw-gap-2 tw-justify-end">
                                <a-action-button icon="solar:pen-2-bold-duotone" intent="secondary" @click="openEdit(p)" />
                                <a-action-button icon="solar:trash-bin-2-bold-duotone" intent="danger" @click="askDelete(p)" />
                            </div>
                        </td>
                    </tr>
                </DataTable>
            </div>

            <div class="tw-my-4"></div>

            <TablePagination v-if="items.length" :has-next="options.current_page < options.last_page"
                :has-previous="options.current_page > 1" @next="fetch(options.current_page + 1)"
                @previous="fetch(options.current_page - 1)" />
        </div>
    </div>

    <ProductFormModal :visible="formVisible" :product="editing" @cancel="formVisible = false" @saved="onSaved" />
    <DeleteModal :visible="deleteVisible" :loading="deleting" @cancel="deleteVisible = false" @confirm="confirmDelete" />
</template>

<script setup>
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { paginate, remove } from '@/api/products';
import PageHeader from '@/components/PageHeader.vue';
import StatusBadge from '@/components/StatusBadge.vue';
import FormField from '@/components/form/FormField';
import DataTable from '@/components/table/DataTable.vue';
import TablePagination from '@/components/table/TablePagination.vue';
import DeleteModal from '@/components/modals/DeleteModal.vue';
import ProductFormModal from './ProductFormModal.vue';
import { useFormat } from '@/composables/useFormat';
import { rowClass, cellClass } from '@/composables/useTableStyles';
import useAlert from '@/composables/useAlert';

const i18n = useI18n();
const fmt = useFormat();

const columns = computed(() => [
    { label: i18n.t('fields.name') },
    { label: i18n.t('portal.fields.type'), classes: ['tw-w-[110px]'] },
    { label: i18n.t('portal.fields.category'), classes: ['tw-w-[130px]'] },
    { label: i18n.t('fields.product-sku'), classes: ['tw-w-[130px]'] },
    { label: i18n.t('fields.selling-price'), classes: ['tw-w-[120px]'] },
    { label: i18n.t('fields.buying-price'), classes: ['tw-w-[120px]'] },
    { label: i18n.t('fields.status'), classes: ['tw-w-[110px]'] },
    { label: i18n.t('fields.actions'), classes: ['tw-w-[110px]', 'tw-text-end'] },
]);

const items = ref([]);
const options = ref({ current_page: 1, last_page: 1 });
const loading = ref(false);
const search = ref('');

const formVisible = ref(false);
const editing = ref(null);
const deleteVisible = ref(false);
const deleting = ref(false);
const toDelete = ref(null);

async function fetch(page = 1) {
    loading.value = true;
    try {
        const { data } = await paginate({ per_page: 10, page, search: search.value });
        const { data: rows, ...ops } = data.data;
        items.value = rows;
        options.value = ops;
    } catch (e) { /* interceptor */ } finally {
        loading.value = false;
    }
}

const openCreate = () => { editing.value = null; formVisible.value = true; };
const openEdit = (p) => { editing.value = { ...p }; formVisible.value = true; };

const onSaved = () => { formVisible.value = false; fetch(options.value.current_page); };

const askDelete = (p) => { toDelete.value = p; deleteVisible.value = true; };
const confirmDelete = async () => {
    deleting.value = true;
    try {
        await remove(toDelete.value.id);
        useAlert(i18n.t('messages.deleted-successfully', { field: i18n.t('portal.entities.product') }));
        deleteVisible.value = false;
        fetch(options.value.current_page);
    } catch (e) { /* interceptor */ } finally {
        deleting.value = false;
    }
};

fetch();
</script>
